/**
 * Admin User Invite Page
 *
 * Form for inviting a new user to a tenant.
 *
 * @fileoverview Admin user invite page component
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  useColorModeValue,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { MdChevronRight, MdSend, MdArrowBack } from 'react-icons/md';
import { useAllAdminTenants } from '../../../hooks/useAdminTenants';
import { useInviteAdminUser } from '../../../hooks/useAdminUsers';
import type { TenantRole } from '../../../types/admin';

/**
 * Validate email format
 *
 * @param email - Email string
 * @returns Whether email is valid
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Admin User Invite Page
 *
 * Form for inviting a new user to a specific tenant.
 *
 * @returns React component
 *
 * @example
 * <AdminUserInvite />
 */
export const AdminUserInvite: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const selectBg = useColorModeValue('white', 'gray.700');
  const selectColor = useColorModeValue('gray.800', 'white');
  const optionBg = useColorModeValue('white', 'gray.700');

  // Common styles for Select components
  const selectStyles = {
    bg: selectBg,
    color: selectColor,
    sx: {
      '& option': {
        bg: optionBg,
        color: selectColor,
      },
    },
  };

  // Form state
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenantRole, setTenantRole] = useState<TenantRole>('USER');
  const [tenantSearch, setTenantSearch] = useState('');
  const [touched, setTouched] = useState({ email: false });

  const tenantQuery = useMemo(
    () => ({
      isActive: true,
      sortBy: 'name' as const,
      sortOrder: 'asc' as const,
    }),
    []
  );

  // Hooks
  const { tenants, loading: tenantsLoading } = useAllAdminTenants(tenantQuery);
  const { inviteUser, loading, error, clearError } = useInviteAdminUser();

  const filteredTenants = useMemo(() => {
    const query = tenantSearch.trim().toLowerCase();
    if (!query) {
      return tenants;
    }

    return tenants.filter((tenant) => {
      return (
        tenant.name.toLowerCase().includes(query) ||
        tenant.slug.toLowerCase().includes(query) ||
        (tenant.managerEmail?.toLowerCase().includes(query) ?? false)
      );
    });
  }, [tenants, tenantSearch]);

  // Validation
  const emailError = touched.email && !email ? 'Email is required' :
                     touched.email && !isValidEmail(email) ? 'Invalid email format' : '';
  const tenantError = '';

  const isValid = email && isValidEmail(email);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!isValid) {
      setTouched({ email: true });
      return;
    }

    const result = await inviteUser({
      email,
      tenantId,
      tenantRole,
    });

    if (result) {
      toast({
        title: 'Invite sent',
        description: `Invitation sent to ${email}`,
        status: 'success',
        duration: 5000,
      });
      navigate('/admin/users');
    }
  }, [email, tenantId, tenantRole, isValid, inviteUser, toast, navigate, clearError]);

  /**
   * Handle field blur
   */
  const handleBlur = (field: 'email') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Get selected tenant name for confirmation
  const selectedTenant = tenants.find((t) => t.id === tenantId);

  return (
    <Box p={6}>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<Icon as={MdChevronRight} color="gray.500" />}
        mb={6}
      >
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/admin/users">
            Users
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Invite User</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <HStack spacing={4} mb={6}>
        <Button
          variant="ghost"
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/users')}
        >
          Back
        </Button>
        <Heading size="lg">Invite User</Heading>
      </HStack>

      <Card bg={bgColor} border="1px" borderColor={borderColor} maxW="600px" mx="auto">
        <CardHeader>
          <Heading size="md">New User Invitation</Heading>
          <Text color="gray.500" mt={2}>
            Send an invitation email to a new user to join a specific tenant.
          </Text>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={5} align="stretch">
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Users must be invited to a specific tenant. They cannot exist without a tenant membership.
                </Text>
              </Alert>

              <FormControl isRequired isInvalid={!!emailError}>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="user@example.com"
                />
                {emailError ? (
                  <FormErrorMessage>{emailError}</FormErrorMessage>
                ) : (
                  <FormHelperText>
                    The email address where the invitation will be sent.
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl isInvalid={!!tenantError}>
                <FormLabel>Tenant</FormLabel>
                <Input
                  mb={3}
                  value={tenantSearch}
                  onChange={(e) => setTenantSearch(e.target.value)}
                  placeholder="Search tenant by name, slug or manager email"
                  isDisabled={tenantsLoading || tenants.length === 0}
                />
                <Select
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  onBlur={() => {}}
                  placeholder={tenantsLoading ? 'Loading tenants...' : 'Select a tenant'}
                  isDisabled={tenantsLoading}
                  {...selectStyles}
                >
                  {filteredTenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </Select>
                {tenantError ? (
                  <FormErrorMessage>{tenantError}</FormErrorMessage>
                ) : (
                  <FormHelperText>
                    Showing {filteredTenants.length} of {tenants.length} tenants.
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Role in Tenant</FormLabel>
                <Select
                  value={tenantRole}
                  onChange={(e) => setTenantRole(e.target.value as TenantRole)}
                  {...selectStyles}
                >
                  <option value="USER">User</option>
                  <option value="TENANT_ADMIN">Tenant Admin</option>
                </Select>
                <FormHelperText>
                  The role the user will have within the tenant.
                </FormHelperText>
              </FormControl>

              {selectedTenant && (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    <Text as="span" fontWeight="bold">{email || 'User'}</Text> will be invited to join{' '}
                    <Text as="span" fontWeight="bold">{selectedTenant.name}</Text> as a{' '}
                    <Text as="span" fontWeight="bold">
                      {tenantRole === 'TENANT_ADMIN' ? 'Tenant Admin' : 'User'}
                    </Text>.
                  </Text>
                </Alert>
              )}

              <HStack spacing={3} pt={4}>
                <Button
                  type="submit"
                  colorScheme="blue"
                  leftIcon={<Icon as={MdSend} />}
                  isLoading={loading}
                  isDisabled={!isValid}
                >
                  Send Invitation
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/users')}
                >
                  Cancel
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AdminUserInvite;
